/**
 * Grading API Service
 * Handles submissions and grading
 */

import { labManagerApi } from './apiClient';
import { mockFallback } from '../lib/mockFallback';
import { requirePaginatedResponse } from './responseValidation';
import type {
  Submission,
  GradingStats,
  GradeInput,
  PaginatedResponse,
  RubricItem,
} from '../types';

interface SubmissionListParams {
  page?: number;
  limit?: number;
  labId?: string;
  cohortId?: string;
  userId?: string;
  status?: 'pending' | 'grading' | 'graded';
}

/**
 * Get submissions with filters
 */
export async function getSubmissions(params: SubmissionListParams = {}): Promise<PaginatedResponse<Submission>> {
  try {
    const response = await labManagerApi.get('/grading/submissions', { params });
    return requirePaginatedResponse<Submission>(response.data, 'submissions', ['id', 'labId', 'labTitle', 'userId', 'studentName', 'status', 'submittedAt']);
  } catch (error) {
    console.error('Failed to fetch submissions:', error);
    // Return mock data for development
    const mockSubmissions: Submission[] = [
      {
        id: 's1',
        labId: '1',
        labTitle: 'Introduction to OpenAI API',
        userId: 'u1',
        studentName: 'John Doe',
        studentEmail: 'john@example.com',
        status: 'pending',
        submittedAt: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
        files: [
          { path: 'main.py', content: 'import openai\n\n# Solution code here...' },
          { path: 'test.py', content: 'import pytest\n\n# Tests here...' },
        ],
        testResults: {
          passed: 4,
          failed: 1,
          total: 5,
          details: [
            { name: 'test_api_connection', passed: true },
            { name: 'test_completion', passed: true },
            { name: 'test_streaming', passed: true },
            { name: 'test_error_handling', passed: false, message: 'Expected exception not raised' },
            { name: 'test_rate_limiting', passed: true },
          ],
        },
        autoScore: 80,
      },
      {
        id: 's2',
        labId: '2',
        labTitle: 'Building RAG Applications',
        userId: 'u2',
        studentName: 'Jane Smith',
        studentEmail: 'jane@example.com',
        status: 'pending',
        submittedAt: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
        files: [
          { path: 'rag.py', content: 'from langchain import ...' },
        ],
        testResults: {
          passed: 8,
          failed: 0,
          total: 8,
          details: [],
        },
        autoScore: 100,
      },
      {
        id: 's3',
        labId: '3',
        labTitle: 'Fine-tuning Language Models',
        userId: 'u3',
        studentName: 'Bob Johnson',
        studentEmail: 'bob@example.com',
        status: 'graded',
        submittedAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
        files: [],
        finalScore: 85,
        feedback: 'Good implementation with minor improvements needed.',
        gradedBy: 'instructor@example.com',
        gradedAt: new Date(Date.now() - 1000 * 60 * 60 * 12).toISOString(),
      },
    ];

    let filtered = mockSubmissions;
    if (params.status) {
      filtered = filtered.filter((s) => s.status === params.status);
    }
    if (params.labId) {
      filtered = filtered.filter((s) => s.labId === params.labId);
    }
    if (params.userId) filtered = filtered.filter((s) => s.userId === params.userId);

    const mock = {
      data: filtered,
      pagination: {
        page: params.page || 1,
        limit: params.limit || 20,
        total: filtered.length,
        totalPages: Math.ceil(filtered.length / (params.limit || 20)),
      },
    };
    return mockFallback(error, mock);
  }
}

/**
 * Get pending submissions
 */
export async function getPendingSubmissions(params?: Omit<SubmissionListParams, 'status'>): Promise<PaginatedResponse<Submission>> {
  return getSubmissions({ ...params, status: 'pending' });
}

/**
 * Get single submission
 */
export async function getSubmission(submissionId: string): Promise<Submission> {
  try {
    const response = await labManagerApi.get(`/grading/submissions/${submissionId}`);
    return response.data.data;
  } catch (error) {
    console.error('Failed to fetch submission:', error);
    throw error;
  }
}

/**
 * Grade a submission
 */
export async function gradeSubmission(submissionId: string, data: GradeInput): Promise<Submission> {
  // GradeInput already matches the lab-manager contract
  // (gradingController.gradeSubmission expects { rubricScores, totalGrade,
  // feedback, expectedReviewVersion }, and validates the server-bound review), so forward it
  // straight through.
  const response = await labManagerApi.post(`/grading/submissions/${submissionId}/grade`, data);
  // Controller responds with `{ data: submission }` — unwrap the envelope.
  return response.data.data;
}

/**
 * Get grading statistics
 */
export async function getGradingStats(cohortId?: string): Promise<GradingStats> {
  try {
    const response = await labManagerApi.get('/grading/stats', { params: { cohortId } });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch grading stats:', error);
    const mock: GradingStats = {
      pending: 18,
      gradedToday: 12,
      gradedThisWeek: 45,
      averageScore: 82,
      averageGradingTime: 8,
      byLab: [
        { labId: '1', labTitle: 'Introduction to OpenAI API', pending: 3, graded: 42, avgScore: 87 },
        { labId: '2', labTitle: 'Building RAG Applications', pending: 5, graded: 25, avgScore: 78 },
        { labId: '3', labTitle: 'Fine-tuning Language Models', pending: 4, graded: 12, avgScore: 72 },
        { labId: '5', labTitle: 'LangChain Fundamentals', pending: 2, graded: 35, avgScore: 85 },
      ],
    };
    return mockFallback(error, mock);
  }
}

/**
 * Auto-grade all pending submissions for a lab
 */
export async function autoGradeAll(labId: string): Promise<{ processed: number; passed: number; failed: number }> {
  const response = await labManagerApi.post(`/grading/labs/${labId}/auto-grade`);
  return response.data;
}

/**
 * Export grades to CSV
 */
export async function exportGrades(params: { labId?: string; cohortId?: string; format?: 'csv' | 'xlsx' }): Promise<Blob> {
  const response = await labManagerApi.get('/grading/export', {
    params,
    responseType: 'blob',
  });
  return response.data;
}

/**
 * Get rubric for a lab
 */
export async function getRubric(labId: string): Promise<{ rubric: RubricItem[] }> {
  const response = await labManagerApi.get(`/labs/${labId}/rubric`);
  return response.data;
}
